import { keepPreviousData, skipToken, useMutation, useQuery } from '@tanstack/react-query'

import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query'
import type { DeviceType } from '~/types/device-type'
import type {
  IIapFeatureResource,
  IIapGraph,
  IIapResource,
  IIap,
  IInAppFeature,
  IInAppFeaturesGroup,
  TInAppFeaturePayload,
  IInAppOffer,
  TInAppOfferPayload,
  IInApp,
  IInAppFiltersCount,
  TInAppListingPayload,
  TInAppFiltersPayload,
  IInAppPrice,
  TInAppFeaturesGroupPayload,
  IInAppOfferInList,
  TCreateOfferPayload,
  TUpdateOfferPayload,
} from '~/types/iap'

import {
  createFeature,
  createFeaturesGroup,
  deleteFeature,
  deleteFeaturesGroup,
  getFeaturesGroupList,
  getFeaturesList,
  getOffersList,
  getInAppsList,
  getPrices,
  getResource,
  getWorkflowGraph,
  inAppsDailyRefresh,
  listFiltersData,
  refreshIap,
  refreshInAppsForApp,
  updateFeaturesGroup,
  getFeaturesIdsList,
  getSkusIdsList,
  getOffer,
  createOffer,
  updateOffer,
  deleteOffer,
} from '~/api/iap'
import { ALGOLIA_INDEX_NAMES } from '~/const/algolia'
import { getSearchClient } from '~/utils/algolia'

const searchClient = getSearchClient()

////////// KEYS //////////
export const iapKeys = {
  all: ['in-app-purchases'] as const,
  featureResource: (appId: string, resourceId: string) =>
    [...iapKeys.all, 'feature-resource', appId, resourceId] as const,
  featuresGroupList: (params: { appKey?: string; deviceType?: string; featureId?: string; searchQuery?: string }) =>
    [...iapKeys.all, 'features-group-list', params] as const,
  featuresIdsList: (searchQuery?: string) => [...iapKeys.all, 'features-ids-list', searchQuery] as const,
  featuresList: (appKey?: string, deviceType?: string) =>
    [...iapKeys.all, 'features-ids-list', appKey, deviceType] as const,
  filtersData: (params: TInAppFiltersPayload) => [...iapKeys.all, 'filters-data', params] as const,
  iapResource: (appId: string, resourceId: string) => [...iapKeys.all, 'iap-resource', appId, resourceId] as const,
  inAppsList: (params: TInAppListingPayload) => [...iapKeys.all, 'in-apps-list', params] as const,
  listByObjectId: (objectIds: string[]) => [...iapKeys.all, 'list-by-object-id', ...objectIds] as const,
  offerDetails: (offerId?: string) => [...iapKeys.all, 'offer-details', offerId] as const,
  offersList: (appsInfos?: TInAppOfferPayload, searchQuery?: string) =>
    [...iapKeys.all, 'offers-list', appsInfos, searchQuery] as const,
  prices: (params: { appKey?: string; deviceType?: DeviceType; sku?: string }) =>
    [...iapKeys.all, 'prices', params] as const,
  skusIdsList: (searchQuery?: string) => [...iapKeys.all, 'skus-ids-list', searchQuery] as const,
  workflowGraph: (appId: string) => [...iapKeys.all, 'workflow-graph', appId] as const,
}

////////// QUERIES //////////
export const useIapListBySkuQuery = (
  {
    appKey,
    deviceType,
    skus,
  }: {
    appKey: string
    deviceType: string
    skus?: string[]
  },
  { enabled }: { enabled: boolean }
): UseQueryResult<IIap[], unknown> => {
  const objectIds = skus?.map((sku) => `${appKey}_${deviceType}_${sku}`) || []

  return useQuery({
    enabled,
    queryFn: async () => {
      const subscriptionsIndex = searchClient.initIndex(ALGOLIA_INDEX_NAMES.IAP_SUBSCRIPTIONS)
      const productsIndex = searchClient.initIndex(ALGOLIA_INDEX_NAMES.IAP_PRODUCTS)
      const { results: subscriptionResults } = await subscriptionsIndex.getObjects(objectIds)
      const { results: productResults } = await productsIndex.getObjects(objectIds)
      return subscriptionResults.filter((value) => !!value).concat(productResults.filter((value) => !!value))
    },
    queryKey: iapKeys.listByObjectId(objectIds),
  })
}

export const useIapWorkflowGraphQuery = ({ appId }: { appId?: string }): UseQueryResult<IIapGraph, unknown> => {
  return useQuery({
    enabled: !!appId,
    queryFn: () => getWorkflowGraph(appId as string),
    queryKey: iapKeys.workflowGraph(appId as string),
  })
}

export const useIapResourceQuery = ({
  appId,
  resourceId,
}: {
  appId?: string
  resourceId?: string
}): UseQueryResult<IIapResource[], unknown> => {
  return useQuery({
    enabled: !!appId && !!resourceId,
    queryFn: () => getResource(appId as string, resourceId as string) as Promise<IIapResource[]>,
    queryKey: iapKeys.iapResource(appId as string, resourceId as string),
  })
}

export const useIapFeatureResourceQuery = (
  {
    appId,
    resourceId,
  }: {
    appId?: string
    resourceId?: string
  },
  settings: { enabled: boolean }
): UseQueryResult<IIapFeatureResource, unknown> & { isLoadingAndEnabled: boolean } => {
  const query = useQuery({
    enabled: !!appId && !!resourceId && settings.enabled,
    queryFn: () => getResource(appId as string, resourceId as string) as Promise<IIapFeatureResource>,
    queryKey: iapKeys.featureResource(appId as string, resourceId as string),
  })

  return { ...query, isLoadingAndEnabled: query.isLoading && !!settings.enabled }
}

export const useRefreshIapMutation = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: refreshIap })
}

///////////////////// REWORK IAP API BELOW /////////////////////

////////// QUERIES //////////

export const useInAppsListQuery = (params: TInAppListingPayload): UseQueryResult<IPaginatedResponse<IInApp>> => {
  return useQuery({
    queryFn: () => getInAppsList(params),
    queryKey: iapKeys.inAppsList(params),
  })
}

export const useFiltersDataListQuery = (params: TInAppFiltersPayload): UseQueryResult<IInAppFiltersCount> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => listFiltersData(params),
    queryKey: iapKeys.filtersData(params),
  })
}

export const usePricesQuery = ({
  appKey,
  deviceType,
  sku,
}: {
  appKey?: string
  deviceType?: DeviceType
  sku?: string
}): UseQueryResult<IInAppPrice[]> => {
  return useQuery({
    queryFn: appKey && deviceType && sku ? () => getPrices({ appKey, deviceType, sku }) : skipToken,
    queryKey: iapKeys.prices({ appKey, deviceType, sku }),
  })
}

export const useFeaturesListQuery = ({
  appKey,
  deviceType,
}: {
  appKey?: string
  deviceType?: DeviceType
}): UseQueryResult<IPaginatedResponse<IInAppFeature>> => {
  return useQuery({
    queryFn: appKey && deviceType ? () => getFeaturesList({ appKey, deviceType }) : skipToken,
    queryKey: iapKeys.featuresList(appKey, deviceType),
  })
}

export const useFeaturesIdsListQuery = (searchQuery?: string): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: () => getFeaturesIdsList(searchQuery),
    queryKey: iapKeys.featuresIdsList(searchQuery),
  })
}

export const useFeaturesGroupListQuery = ({
  appKey,
  deviceType,
  featureId,
  searchQuery,
}: {
  appKey?: string
  deviceType?: DeviceType
  featureId?: string
  searchQuery?: string
}): UseQueryResult<IPaginatedResponse<IInAppFeaturesGroup>> => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryFn:
      appKey && deviceType ? () => getFeaturesGroupList({ appKey, deviceType, featureId, searchQuery }) : skipToken,
    queryKey: iapKeys.featuresGroupList({ appKey, deviceType, featureId, searchQuery }),
  })
}

export const useOffersListQuery = ({
  appsInfos,
  limit,
  page,
  searchQuery,
}: {
  appsInfos: TInAppOfferPayload
  limit: number
  page: number
  searchQuery: string
}): UseQueryResult<IPaginatedResponse<IInAppOfferInList>> => {
  return useQuery({
    queryFn: () => getOffersList({ appsInfos, limit, page, searchQuery }),
    queryKey: iapKeys.offersList(appsInfos, searchQuery),
  })
}

export const useOfferDetailsQuery = ({ offerId }: { offerId?: string }): UseQueryResult<IInAppOffer> => {
  return useQuery({
    queryFn: offerId ? () => getOffer({ offerId }) : skipToken,
    queryKey: iapKeys.offerDetails(offerId),
  })
}

export const useSkusIdsListQuery = (searchQuery?: string): UseQueryResult<string[]> => {
  return useQuery({
    queryFn: () => getSkusIdsList(searchQuery),
    queryKey: iapKeys.skusIdsList(searchQuery),
  })
}

////////// MUTATIONS //////////

export const useCreateFeatureMutation = (): UseMutationResult<void, unknown, TInAppFeaturePayload, unknown> => {
  return useMutation({ mutationFn: createFeature })
}

export const useDeleteFeatureMutation = (): UseMutationResult<void, unknown, TInAppFeaturePayload, unknown> => {
  return useMutation({ mutationFn: deleteFeature })
}

export const useRefreshInAppsForAppMutation = (): UseMutationResult<
  void,
  unknown,
  { appKey: string; deviceType: DeviceType },
  unknown
> => {
  return useMutation({ mutationFn: refreshInAppsForApp })
}

export const useInAppsDailyRefreshMutation = (): UseMutationResult<void, unknown, unknown, unknown> => {
  return useMutation({ mutationFn: inAppsDailyRefresh })
}

export const useCreateFeaturesGroupMutation = (): UseMutationResult<
  void,
  unknown,
  TInAppFeaturesGroupPayload,
  unknown
> => {
  return useMutation({ mutationFn: createFeaturesGroup })
}

export const useUpdateFeaturesGroupMutation = (): UseMutationResult<
  IInAppFeaturesGroup,
  unknown,
  Omit<TInAppFeaturePayload, 'featureId'> & { featureGroupId: string; featureIds: string[]; name: string },
  unknown
> => {
  return useMutation({ mutationFn: updateFeaturesGroup })
}

export const useDeleteFeaturesGroupMutation = (): UseMutationResult<
  void,
  unknown,
  { featureGroupId: string },
  unknown
> => {
  return useMutation({ mutationFn: deleteFeaturesGroup })
}

export const useCreateOfferMutation = (): UseMutationResult<void, unknown, TCreateOfferPayload, unknown> => {
  return useMutation({ mutationFn: createOffer })
}

export const useUpdateOfferMutation = (): UseMutationResult<IInAppOffer, unknown, TUpdateOfferPayload, unknown> => {
  return useMutation({ mutationFn: updateOffer })
}

export const useDeleteOfferMutation = (): UseMutationResult<void, unknown, string, unknown> => {
  return useMutation({ mutationFn: deleteOffer })
}
